import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Leaf, Mail, ArrowLeft, AlertCircle, CheckCircle2 } from 'lucide-react';
import { COLORS } from '../data/mockData';
import { humanizeApiError } from '../lib/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email'); return; }
    setError(''); setSuccess(''); setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/forgot-password`, { email }, { withCredentials: true });
      setSuccess(r.data?.message || 'If an account exists, we just sent you a reset link.');
    } catch (err) {
      setError(humanizeApiError(err, 'Could not send reset email'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #F9F6F0 0%, #E8ECD7 50%, #F9F6F0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 440 }}>
        <button onClick={() => navigate('/login')} data-testid="back-to-login"
          style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: COLORS.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <ArrowLeft size={24} color={COLORS.textPrimary} />
        </button>

        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <Leaf size={48} color={COLORS.primary} />
          <h1 style={{ fontSize: 30, fontWeight: 800, color: COLORS.textPrimary, marginTop: 14, fontFamily: 'Outfit, sans-serif' }}>Forgot Password</h1>
          <p style={{ fontSize: 15, color: COLORS.textSecondary, marginTop: 6, lineHeight: 1.5 }}>
            Enter your email and we'll send you a single-use reset link (expires in 1 hour).
          </p>
        </div>

        {error && (
          <div data-testid="forgot-error" style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#FDE8E8', borderRadius: 8, padding: 12, marginBottom: 16 }}>
            <AlertCircle size={18} color={COLORS.error} />
            <span style={{ fontSize: 14, color: COLORS.error }}>{error}</span>
          </div>
        )}
        {success && (
          <div data-testid="forgot-success" style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#E8F5E9', borderRadius: 8, padding: 12, marginBottom: 16 }}>
            <CheckCircle2 size={18} color="#3CB371" />
            <span style={{ fontSize: 14, color: '#1F5132' }}>{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 6, display: 'block', marginLeft: 4 }}>Email</label>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '0 16px', height: 56, gap: 10 }}>
              <Mail size={20} color={COLORS.textSecondary} />
              <input type="email" data-testid="forgot-email-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com"
                style={{ flex: 1, border: 'none', fontSize: 16, color: COLORS.textPrimary, backgroundColor: 'transparent', outline: 'none', fontFamily: 'Work Sans, sans-serif' }} />
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="forgot-submit-btn"
            style={{ backgroundColor: COLORS.primary, borderRadius: 9999, padding: '16px', border: 'none', cursor: loading ? 'wait' : 'pointer', marginTop: 8, opacity: loading ? 0.7 : 1, transition: 'opacity 0.2s' }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: '#FFF' }}>{loading ? 'Sending…' : 'Send Reset Link'}</span>
          </button>

          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <span style={{ fontSize: 15, color: COLORS.textSecondary }}>Remembered it? </span>
            <Link to="/login" style={{ fontSize: 15, fontWeight: 700, color: COLORS.primary, textDecoration: 'none' }}>Sign In</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
